import React, { useState } from 'react';
import { RiasecLastTestResponse, RiasecTestData, RiasecType, RiasecCategoryInfo, RiasecProfession } from '../../typings/riasec';
import RadarChart from '../RadarChart';
import RiasecPdfDownload from './RiasecPdfDownload';
import { riasecService } from '../../services/riasec.service';
import Button from '../UI/Button';
import { ChevronDown, ChevronUp, Clock, RefreshCcw, CheckCircle2, AlertCircle, Loader2, Hourglass } from "lucide-react";
import { formatBold } from "../../utils/vak";

interface RiasecTestResultProps {
  testResult: RiasecLastTestResponse;
  professions: RiasecProfession[];
  onBackToHome: () => void;
}

const RIASEC_CATEGORIES: RiasecCategoryInfo[] = [
  { type: 'R', name: 'Realistic', title: 'Realistic (Doer)', description: 'Suka bekerja dengan tangan, alat, dan mesin', color: '#FF6384' },
  { type: 'I', name: 'Investigative', title: 'Investigative (Thinker)', description: 'Suka menganalisis, meneliti, dan memecahkan masalah', color: '#36A2EB' },
  { type: 'A', name: 'Artistic', title: 'Artistic (Creator)', description: 'Suka berkreasi, berimajinasi, dan berekspresi', color: '#FFCE56' },
  { type: 'S', name: 'Social', title: 'Social (Helper)', description: 'Suka membantu, mengajar, dan berinteraksi dengan orang', color: '#4BC0C0' },
  { type: 'E', name: 'Enterprising', title: 'Enterprising (Persuader)', description: 'Suka memimpin, menjual, dan mempengaruhi orang lain', color: '#9966FF' },
  { type: 'C', name: 'Conventional', title: 'Conventional (Organizer)', description: 'Suka mengorganisir, mengatur data, dan mengikuti prosedur', color: '#FF9F40' }
];

const RiasecTestResult: React.FC<RiasecTestResultProps> = ({
  testResult: initialTestResult,
  professions,
  onBackToHome
}) => {
  const [testResult, setTestResult] = useState<RiasecLastTestResponse>(initialTestResult);
  console.log('test', initialTestResult)
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [expandDescAI, setExpandDescAI] = useState<boolean>(false);

  const test = testResult.data.test;

  React.useEffect(() => {
    let intervalId: any;
    let startTime = Date.now();

    if (!test.ai_generated) {
      intervalId = setInterval(async () => {
        const elapsedTime = (Date.now() - startTime) / 1000;
        if (elapsedTime >= 30) {
          clearInterval(intervalId);
          return;
        }

        try {
          const response = await riasecService.getTestById(test.id);
          if (response.data.test.ai_generated) {
            setTestResult(response);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Auto refresh error:", err);
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [test.id, test.ai_generated]);

  const getRadarData = (testData: RiasecTestData) => {
    return {
      labels: ['Realistic', 'Investigative', 'Artistic', 'Social', 'Enterprising', 'Conventional'],
      datasets: [
        {
          label: 'RIASEC Score',
          data: [testData.r, testData.i, testData.a, testData.s, testData.e, testData.c],
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        },
      ],
    };
  };

  const getProfessionsByType = (type: RiasecType) => {
    return professions.filter(profession => profession.type === type);
  };

  const toggleExpanded = (categoryType: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryType]: !prev[categoryType]
    }));
  };

  const getTop3Categories = () => {
    return RIASEC_CATEGORIES
      .map(category => ({
        ...category,
        score: testResult.data.test[category.type.toLowerCase() as keyof RiasecTestData] as number
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const handleRefreshTestData = async () => {
    setIsRefreshing(true);
    setAiError(null);
    
    try {
      const response = await riasecService.getTestById(testResult.data.test.id);
      setTestResult(response);
    } catch (error) {
      setAiError('Gagal memuat ulang data. Silakan coba lagi.');
      console.error('Error refreshing test data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="riasec-container">
      <div className="w-full">
        <div className="flex flex-wrap">
          <div className="w-full">
            <div className="bg-white rounded-lg shadow border riasec-card">
              <div className="p-4">
                <h5 className="text-lg font-semibold mb-2">Hasil Tes IDE</h5>

                <div className="flex flex-wrap mt-4">
                  <div className="md:w-6/12">
                    <h6>Grafik IDE</h6>
                    <div style={{ height: "300px" }}>
                      <RadarChart data={getRadarData(testResult.data.test)} />
                    </div>
                  </div>

                  <div className="md:w-6/12">
                    <h6>Detail Skor IDE</h6>
                    <div className="riasec-scores">
                      {getTop3Categories().map((category, index) => {
                        const categoryProfessions = getProfessionsByType(
                          category.type
                        );
                        const isExpanded = expandedCategories[category.type];
                        const displayProfessions = isExpanded
                          ? categoryProfessions
                          : categoryProfessions.slice(0, 4);
                        const hasMore = categoryProfessions.length > 4;

                        return (
                          <div
                            key={category.type}
                            className="score-item mb-3 pb-2 border-b"
                          >
                            <div className="flex justify-between items-center mb-1">
                              <span style={{ color: category.color }}>
                                <strong>
                                  #{index + 1} {category.title}
                                </strong>
                              </span>
                              <span
                                className="inline-block px-2 py-1 text-xs font-medium rounded"
                                style={{
                                  backgroundColor: category.color,
                                  color: "#fff",
                                  fontWeight: "bold",
                                }}
                              >
                                {category.score}
                              </span>
                            </div>
                            <small className="text-gray-500 block mb-2">
                              {category.description}
                            </small>

                            {categoryProfessions.length > 0 && (
                              <div className="mt-2">
                                <small className="text-gray-500">
                                  Profesi yang cocok:
                                </small>
                                <div className="mt-1">
                                  <small>
                                    {displayProfessions.map((prof, idx) => (
                                      <span key={prof.id}>
                                        {prof.profession}
                                        {idx < displayProfessions.length - 1 &&
                                          ", "}
                                      </span>
                                    ))}
                                    {!isExpanded && hasMore && (
                                      <span>
                                        ...{" "}
                                        <button
                                          className="btn btn-link btn-sm p-0 ml-1"
                                          style={{
                                            fontSize: "0.875rem",
                                            verticalAlign: "baseline",
                                          }}
                                          onClick={() =>
                                            toggleExpanded(category.type)
                                          }
                                        >
                                          Lihat lebih banyak
                                        </button>
                                      </span>
                                    )}
                                    {isExpanded && hasMore && (
                                      <span>
                                        {" "}
                                        <button
                                          className="btn btn-link btn-sm p-0 ml-1"
                                          style={{
                                            fontSize: "0.875rem",
                                            verticalAlign: "baseline",
                                          }}
                                          onClick={() =>
                                            toggleExpanded(category.type)
                                          }
                                        >
                                          Lihat lebih sedikit
                                        </button>
                                      </span>
                                    )}
                                  </small>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* School Recommendation Section */}
                <div className="mt-4"></div>

                {/* AI Recommendation Section */}
                <div className="mt-4">
                  {!test.ai_generated ? (
                    <div
                      className="p-4 rounded border bg-amber-50 border-amber-200 text-amber-800 flex flex-col md:flex-row justify-between items-center gap-4"
                      role="alert"
                    >
                      <div className="flex gap-4 items-start">
                        <div className="bg-amber-100 p-3 rounded-full">
                          <Hourglass
                            className={`w-6 h-6 text-amber-600 animate-pulse`}
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-amber-900 mb-0">
                            Analisis sedang dalam proses
                          </h4>
                          <p className="mb-0 mt-1 text-sm">
                            Laporan mendalam sedang disiapkan oleh AI. Halaman
                            akan diperbarui secara otomatis.
                          </p>
                        </div>
                      </div>
                      <Button
                        warning
                        className="font-bold whitespace-nowrap"
                        onClick={handleRefreshTestData}
                        disabled={isRefreshing}
                      >
                        {isRefreshing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Lihat Hasil"
                        )}
                      </Button>
                    </div>
                  ) : test.ai_generated &&
                    test.ai_jalur &&
                    test.ai_jurusan &&
                    test.ai_alasan_rekomendasi ? (
                    <>
                      <div className="bg-white rounded-lg shadow border">
                        <div className="p-4">
                          <h5 className="text-lg font-semibold mb-2 mb-3 flex justify-between gap-3">
                            <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-primary text-white mr-2 whitespace-normal">
                              {testResult.data.test.ai_jalur}
                            </span>
                            {testResult.data.test.ai_jurusan}
                          </h5>
                          <h6 className="text-sm text-gray-600 mb-2 mb-3 text-gray-500">
                            Alasan Rekomendasi:
                          </h6>
                          <div
                            className="text-gray-700"
                            style={{
                              whiteSpace: "pre-wrap",
                              lineHeight: "1.6",
                              fontSize: "1rem",
                            }}
                          >
                            {testResult?.data?.test?.ai_alasan_rekomendasi &&
                            testResult?.data?.test?.ai_alasan_rekomendasi
                              ?.length > 200 ? (
                              <>
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: expandDescAI
                                      ? formatBold(
                                          testResult.data.test
                                            .ai_alasan_rekomendasi
                                        )
                                      : `${formatBold(
                                          testResult.data.test.ai_alasan_rekomendasi.substring(
                                            0,
                                            200
                                          )
                                        )}...`,
                                  }}
                                />
                                <Button
                                  link
                                  size="sm"
                                  className="p-0 ml-2"
                                  style={{
                                    fontSize: "0.875rem",
                                    verticalAlign: "baseline",
                                  }}
                                  onClick={() => setExpandDescAI(!expandDescAI)}
                                >
                                  {expandDescAI
                                    ? "Lihat lebih sedikit"
                                    : "Lihat selengkapnya"}
                                </Button>
                              </>
                            ) : (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: formatBold(
                                    testResult.data.test.ai_alasan_rekomendasi
                                  ),
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex w-full mt-3">
                        <div
                          className="p-4 rounded border bg-blue-50 border-blue-200 text-blue-800"
                          style={{ fontSize: "0.875rem" }}
                        >
                          <strong>Catatan:</strong> Analisis ini dihasilkan
                          dengan bantuan kecerdasan buatan (AI). Rekomendasi
                          yang diberikan bertujuan membantu eksplorasi awal
                          minat dan potensi siswa. Untuk keputusan yang lebih
                          akurat dan menyeluruh, disarankan melakukan konsultasi
                          lanjutan dengan guru BK, konselor pendidikan, atau
                          ahli terkait.
                        </div>
                      </div>
                    </>
                  ) : null}
                  {aiError && (
                    <div
                      className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mt-2"
                      role="alert"
                    >
                      {aiError}
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <h6>Detail Jawaban</h6>
                  <div
                    className="answers-detail"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {testResult.data.test_questions.map((item, index) => (
                      <div
                        key={item.id}
                        className="answer-item mb-2 p-2 border rounded"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span>
                            <strong>{index + 1}:</strong> {item.question}
                          </span>
                          <span
                            className={`font-bold ${
                              item.answer ? "text-primary" : "text-foreground"
                            }`}
                          >
                            {item.answer ? "Ya" : "Tidak"}
                          </span>
                        </div>
                        <small className="text-gray-500">
                          Kategori: {item.type}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col mt-3">
                  <Button outline className="mb-2" onClick={onBackToHome}>
                    Kembali ke Beranda
                  </Button>
                  <RiasecPdfDownload
                    testId={testResult.data.test.id}
                    testData={testResult.data.test}
                    testDate={testResult.data.test.created_at}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiasecTestResult;
